import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { Field, Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { errorMessage, fieldErrors, useMe } from '../../lib/session';

/** Account and workspace settings. Passwords are changed here, never stored in the client. */
export function SettingsPage() {
  const me = useMe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [mismatch, setMismatch] = useState<string | undefined>(undefined);

  const changePassword = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>('/api/me/password', input),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setDone(false);
    if (next !== confirm) {
      setMismatch('The two new passwords do not match.');
      return;
    }
    setMismatch(undefined);
    changePassword.mutate({ currentPassword: current, newPassword: next });
  };

  const errors = fieldErrors(changePassword.error);

  if (me.isPending) {
    return (
      <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading settings…
      </div>
    );
  }
  if (!me.data) return null;

  const { user, workspace } = me.data;

  return (
    <div className="mx-auto max-w-[760px] px-5 py-5">
      <header className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Settings</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">Your account and this workspace.</p>
      </header>

      <Card className="mb-3">
        <CardHeader title="Account" />
        <CardBody>
          <dl className="space-y-1.5 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-medium text-foreground">{user.name}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-medium text-foreground">{user.email}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Member since</dt>
              <dd className="tabular text-foreground">{new Date(user.createdAt).toLocaleDateString()}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader title="Change password" description="You need your current password to set a new one." />
        <CardBody>
          <form className="max-w-sm space-y-4" onSubmit={onSubmit} noValidate>
            {changePassword.error ? <Alert tone="error">{errorMessage(changePassword.error)}</Alert> : null}
            {done ? (
              <Alert tone="success">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Password updated.
                </span>
              </Alert>
            ) : null}

            <Field label="Current password" error={errors['currentPassword']} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  onChange={(event) => setCurrent(event.target.value)}
                  required
                />
              )}
            </Field>

            <Field label="New password" hint="At least 10 characters." error={errors['newPassword']} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  onChange={(event) => setNext(event.target.value)}
                  required
                />
              )}
            </Field>

            <Field label="Confirm new password" error={mismatch} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  onChange={(event) => setConfirm(event.target.value)}
                  required
                />
              )}
            </Field>

            <Button
              type="submit"
              loading={changePassword.isPending}
              disabled={!current || next.length < 10 || !confirm}
            >
              Update password
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Workspace" />
        <CardBody>
          <dl className="space-y-1.5 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-medium text-foreground">{workspace.name}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Type</dt>
              <dd>
                <Badge tone={workspace.kind === 'demo' ? 'warning' : 'success'}>
                  {workspace.kind === 'demo' ? 'Demo (fixture data)' : 'Production (live sources)'}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Your role</dt>
              <dd className="font-medium text-foreground">{workspace.role}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}
